import AlertBox from '@/components/AlertBox';
import FormFieldComponent from '@/components/FormFieldComponent';
import FormFieldPhone from '@/components/FormFieldPhone';
import { toast } from '@/components/ui/use-toast';
import { genUniqueId } from '@/lib/utils';
import {
  ADDRESS_LINE_VALIDATION,
  CITY_VALIDATION,
  EMAIL_ADDRESS_VALIDATION,
  STATE_VALIDATION,
  ZIP_CODE_VALIDATION,
} from '@/utils/constant';
import { Plus, Trash2 } from 'lucide-react';
import { Dispatch, SetStateAction, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { DocumentType, vendorDetailsSchema } from './vendorSchema';
import { FormFieldSelect } from '@/components/FormFieldSelect';
import { VendorStatus } from '@/types/vendor.types';
import { useUserStore } from '@/stores/user';
import { isAdminOrSupervisor } from '@/services/userService';

export const VendorDetailsStep = ({
  mode,
  documents,
  setDocuments,
}: {
  mode: 'CREATE' | 'EDIT' | 'VIEW';
  documents: DocumentType[];
  setDocuments: Dispatch<SetStateAction<DocumentType[]>>;
}) => {
  const user = useUserStore((state) => state.user);
  const { control, getValues, setValue } = useFormContext<z.infer<typeof vendorDetailsSchema>>();
  const [displayAlertDialog, setDisplayAlertDialog] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<DocumentType | null>(null);
  return (
    <div className="grid-cols-1 w-full gap-6 grid">
      <FormFieldComponent
        name="name"
        label="Vendor Name"
        placeholder="Enter Vendor Name"
        required
        control={control}
        capitalizeFirstLetter
        maxLength={150}
        allowNumber={false}
        autofocus={mode === 'CREATE'}
        allowSpecialCharacters={false}
      />
      <FormFieldComponent
        name="address1"
        label="Address Line 1"
        placeholder="Enter Address Line 1"
        required
        control={control}
        {...ADDRESS_LINE_VALIDATION}
      />
      <FormFieldComponent
        name="address2"
        label="Address Line 2"
        placeholder="Enter Apt, suite etc. (Optional)"
        control={control}
      />
      <div className="grid grid-cols-3 gap-6">
        <FormFieldComponent
          name="city"
          label="City"
          placeholder="City"
          control={control}
          required
          {...CITY_VALIDATION}
        />
        <FormFieldComponent
          control={control}
          name="state"
          label="State"
          placeholder="State"
          required
          {...STATE_VALIDATION}
        />
        <FormFieldComponent
          name="zipCode"
          label="Zip Code"
          placeholder="Zip Code"
          control={control}
          required
          {...ZIP_CODE_VALIDATION}
        />
      </div>
      <div className="grid grid-cols-2 gap-6">
        <FormFieldPhone
          name="phone"
          label="Phone Number"
          required
          itemClassName="w-full"
          placeholder="Enter Phone Number"
        />
        <FormFieldComponent
          name="email"
          label="Email Address"
          placeholder="Enter Email Address"
          control={control}
          required
          {...EMAIL_ADDRESS_VALIDATION}
        />
      </div>
      <div className="grid grid-cols-2 gap-6">
        <FormFieldComponent
          name="taxId"
          label="Tax ID"
          control={control}
          required
          placeholder="Enter Tax ID"
          allowSpecialCharacters={false}
          replaceRegex={/[^0-9]/g}
        />
        <FormFieldComponent
          name="fees"
          label="Fees"
          placeholder="Enter Fees"
          control={control}
          type="number"
          required
        />
      </div>
      <FormFieldComponent
        name="coverageAreas"
        label="Coverage Area"
        placeholder="Enter Coverage Area"
        required
        control={control}
        replaceRegex={/[^a-zA-Z0-9, ]/g}
      />
      <FormFieldComponent
        name="notes"
        label="Notes"
        placeholder="Enter Notes"
        control={control}
        capitalizeFirstLetter
      />
      {mode === 'EDIT' && isAdminOrSupervisor(user) && (
        <div className="grid grid-cols-2 gap-6">
          <FormFieldSelect
            name="status"
            label="Status"
            placeholder="Select Status"
            options={[
              { label: 'Active', value: VendorStatus.ACTIVE },
              { label: 'Inactive', value: VendorStatus.INACTIVE },
            ]}
          />
        </div>
      )}
      <div className="bg-[#F5F5F5] w-full flex flex-col gap-4 rounded-md p-4">
        <div className="flex flex-row justify-between font-semibold text-base">
          <div className="flex flex-row ">
            <span>Documents</span>
          </div>
          <Plus
            className="h-5 w-5 text-placeholder cursor-pointer"
            data-testid="add-document-button"
            onClick={() => {
              const updatedDocuments = [...documents, { id: genUniqueId(), name: '', document: null }];
              setDocuments(updatedDocuments);
              setValue('documents', updatedDocuments, { shouldDirty: false });
            }}
          />
        </div>
        <div className="flex flex-col gap-6">
          {documents.map((doc, index) => (
            <div key={doc.id} className="flex flex-row gap-4">
              <div className="w-[40vw]">
                <FormFieldComponent
                  name={`documents.${index}.name`}
                  defaultValue={doc.name || ''}
                  label="Document Name"
                  required={getValues(`documents.${index}.document`) ?? doc.mediaData ? true : false}
                  placeholder="Enter Document Name"
                  control={control}
                  capitalizeFirstLetter
                  onChange={(e) => {
                    const updatedDocuments = [...documents];
                    updatedDocuments[index] = {
                      ...updatedDocuments[index],
                      name: e.target.value ?? null,
                    };
                    setDocuments(updatedDocuments);
                    setValue(`documents.${index}.name`, e.target.value ?? null, { shouldDirty: true });
                  }}
                />
              </div>
              <div className="w-[40vw]">
                <FormFieldComponent
                  type={'file'}
                  name={`documents.${index}.document`}
                  control={control}
                  label={'\u00A0'}
                  defaultFileName={doc.mediaData?.media?.metadata.fileName ?? 'Click to upload a file'}
                  acceptedFileTypes={['pdf', 'csv', 'xlsx']}
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null;
                    const docIndex = documents.findIndex((d) => d.id === doc.id);
                    if (docIndex === -1) return;
                    const updatedDocuments = [...documents];
                    updatedDocuments[docIndex] = {
                      ...updatedDocuments[docIndex],
                      document: file,
                      mediaId: null,
                      mediaData: undefined,
                    };
                    setDocuments(updatedDocuments);
                    setValue(`documents.${index}.document`, file, { shouldDirty: true });
                  }}
                />
              </div>

              <div className={`w-6 h-[80px] items-center flex-none flex flex-col gap-1`}>
                {<div className="h-10 w-6">{'\u00A0'}</div>}
                <Trash2
                  className="h-5 w-5 text-error cursor-pointer"
                  data-testid="remove-document-button"
                  onClick={() => {
                    if (mode === 'EDIT') {
                      setDocumentToDelete(doc);
                      setDisplayAlertDialog(true);
                    } else {
                      const updatedDocuments = documents.filter((d) => d.id !== doc.id);
                      setDocuments(updatedDocuments);
                      setValue('documents', updatedDocuments ?? null, { shouldDirty: true });
                    }
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      {displayAlertDialog && documentToDelete && (
        <AlertBox
          title={`Are you sure?`}
          description={`Would you like to remove ${documentToDelete.name}?`}
          cancelBtnLabel="No"
          confirmBtnLabel="Yes"
          open={true}
          cancelBtnClick={() => {
            setDisplayAlertDialog(false);
            setDocumentToDelete(null);
          }}
          confirmBtnClick={() => {
            const updatedDocuments = documents.filter((d) => d.id !== documentToDelete.id);
            setDocuments(updatedDocuments);
            setValue(
              'documents',
              updatedDocuments.length
                ? updatedDocuments
                : [{ id: genUniqueId(), name: '', document: null, mediaId: null }],
              { shouldDirty: true, shouldValidate: true }
            );
            setDisplayAlertDialog(false);
            setDocumentToDelete(null);
            toast({
              description: `Document has been removed.`,
            });
          }}
        />
      )}
    </div>
  );
};
